<template>
  <div class="px-3 py-3 pb-10 space-y-4 max-w-screen-2xl mx-auto">
    <!-- Filters -->
    <div class="rounded-2xl border border-gray-200 bg-white p-4 dark:border-gray-800 dark:bg-gray-900">
      <div class="flex items-start justify-between gap-3">
        <div class="min-w-0">
          <h1 class="text-lg font-bold leading-tight text-gray-900 dark:text-gray-50">All Tickets</h1>
          <p class="text-xs text-gray-500 dark:text-gray-400">Browse every ticket across projects and sprints, or filter by project, sprint, or assignee.</p>
        </div>
        <div class="flex shrink-0 items-center gap-1.5">
          <button type="button" @click="clearFilters"
            class="inline-flex items-center gap-1 rounded-lg px-2.5 py-1.5 text-xs font-semibold text-gray-600 transition hover:bg-gray-100 dark:text-gray-300 dark:hover:bg-gray-800">
            <v-icon size="16">mdi-filter-remove-outline</v-icon><span class="hidden sm:inline">Clear</span>
          </button>
          <button type="button" @click="fetchTickets" :disabled="loading"
            class="inline-flex items-center gap-1.5 rounded-lg bg-amber-400 px-3 py-1.5 text-xs font-semibold text-gray-900 shadow-sm transition active:scale-95 disabled:opacity-60">
            <v-icon size="16" :class="loading ? 'animate-spin' : ''">mdi-refresh</v-icon>Refresh
          </button>
        </div>
      </div>

      <div class="mt-3 grid grid-cols-1 gap-2 sm:grid-cols-2 lg:grid-cols-4">
        <v-autocomplete
          density="compact" variant="solo-filled" flat hide-details clearable
          label="Project"
          :items="projectItems"
          item-title="title" item-value="id"
          v-model="filters.project_id"
          @update:modelValue="onProjectChange"
        ></v-autocomplete>
        <v-autocomplete
          density="compact" variant="solo-filled" flat hide-details clearable
          label="Sprint"
          :items="sprintItems"
          item-title="label" item-value="value"
          v-model="filters.sprint_id"
          @update:modelValue="fetchTickets"
        ></v-autocomplete>
        <v-autocomplete
          density="compact" variant="solo-filled" flat hide-details clearable
          label="Assignee"
          :items="assigneeItems"
          item-title="title" item-value="value"
          v-model="filters.assignee"
          @update:modelValue="fetchTickets"
        ></v-autocomplete>
        <v-select
          density="compact" variant="solo-filled" flat hide-details clearable
          label="Status"
          :items="statusItems"
          v-model="filters.status"
          @update:modelValue="fetchTickets"
        ></v-select>
      </div>

      <div class="mt-2 grid grid-cols-1 gap-2 sm:grid-cols-2 lg:grid-cols-4">
        <div class="sm:col-span-2">
          <v-text-field
            density="compact" variant="solo-filled" flat hide-details
            placeholder="Search by ticket ID or title…"
            prepend-inner-icon="mdi-magnify"
            v-model="search"
          ></v-text-field>
        </div>
        <v-text-field
          density="compact" variant="solo-filled" flat hide-details clearable type="date"
          label="Due from"
          v-model="dateFrom"
        ></v-text-field>
        <v-text-field
          density="compact" variant="solo-filled" flat hide-details clearable type="date"
          label="Due to"
          v-model="dateTo"
        ></v-text-field>
      </div>
    </div>

    <!-- Ticket list -->
    <div class="rounded-2xl border border-gray-200 bg-white p-4 dark:border-gray-800 dark:bg-gray-900">
      <div class="mb-2 flex items-center justify-between">
        <span class="text-xs font-semibold text-gray-500 dark:text-gray-400">{{ displayTickets.length }} result{{ displayTickets.length === 1 ? '' : 's' }}</span>
      </div>

      <!-- Loading -->
      <div v-if="loading && !tickets.length" class="space-y-2">
        <div v-for="n in 6" :key="'tl-' + n" class="h-24 animate-pulse rounded-xl bg-gray-100 dark:bg-gray-800"></div>
      </div>

      <!-- Empty -->
      <EmptyState v-else-if="!displayTickets.length" icon="mdi-ticket-outline" title="No tickets match these filters." dense />

      <!-- Ticket cards (search reuses the existing filterRow method) -->
      <div v-else class="space-y-2">
        <div
          v-for="item in displayTickets"
          :key="item.ticket_id"
          v-show="filterRow(null, search, { raw: item })"
          class="rounded-xl border border-gray-200 bg-white p-3 dark:border-gray-800 dark:bg-gray-900"
        >
          <!-- Header: id + priority + status -->
          <div class="flex items-center justify-between gap-2">
            <span class="font-mono text-xs font-bold text-gray-500 dark:text-gray-400">{{ item.ticket_id }}</span>
            <div class="flex items-center gap-1.5">
              <PriorityChip :priority="item.priority" />
              <StatusChip :status="item.status" size="x-small" />
            </div>
          </div>

          <!-- Title -->
          <p class="mt-1.5 text-sm font-semibold leading-snug text-gray-900 dark:text-gray-50">{{ item.name }}</p>

          <!-- Project / sprint meta -->
          <div class="mt-1.5 flex flex-wrap items-center gap-1.5 text-[11px] font-semibold">
            <span v-if="item.project" class="rounded-full bg-slate-100 px-2 py-0.5 text-slate-600 dark:bg-slate-500/15 dark:text-slate-300">{{ item.project.key || item.project.title }}</span>
            <span class="rounded-full bg-gray-100 px-2 py-0.5 text-gray-500 dark:bg-gray-800 dark:text-gray-400">{{ item.sprint ? item.sprint.name : 'Backlog' }}</span>
          </div>

          <!-- Footer: assignees + due + action -->
          <div class="mt-2.5 flex items-center justify-between gap-2 border-t border-gray-100 pt-2 dark:border-gray-800">
            <div class="flex min-w-0 items-center">
              <template v-if="item.assignees_details && item.assignees_details.length">
                <span v-for="a in item.assignees_details.slice(0, 3)" :key="a.id"
                  class="-ml-1 flex h-6 w-6 items-center justify-center rounded-full bg-amber-100 text-[10px] font-bold text-amber-800 ring-2 ring-white first:ml-0 dark:bg-amber-500/20 dark:text-amber-300 dark:ring-gray-900"
                  :title="a.fullname">{{ initials(a.fullname) }}</span>
                <span v-if="item.assignees_details.length > 3" class="ml-1.5 text-xs text-gray-400">+{{ item.assignees_details.length - 3 }}</span>
              </template>
              <span v-else class="text-xs text-gray-400">Unassigned</span>
            </div>
            <div class="flex shrink-0 items-center gap-2">
              <span v-if="item.due_date" class="text-xs text-gray-500 dark:text-gray-400">{{ formattedDate(item.due_date) }}</span>
              <ViewTicket :ticket-data="item" @deleted="fetchTickets" @transferred="fetchTickets" @updated="fetchTickets"></ViewTicket>
            </div>
          </div>
        </div>
      </div>
    </div>

    <Snackbar :toggle-snackbar="isshow" :response-message="message"></Snackbar>
  </div>
</template>

<script>
import axios from 'axios';
import utils from '@/utils';
import constant from '@/constant';
import Snackbar from './Snackbar.vue';
import ViewTicket from './ViewTicket.vue';
import apiMixin from '@/mixins/apiMixin';
import formatMixin from '@/mixins/formatMixin';
import StatusChip from '@/components/ui/StatusChip.vue';
import PriorityChip from '@/components/ui/PriorityChip.vue';
import EmptyState from '@/components/ui/EmptyState.vue';

export default {
  name: 'AllTicketsPage',
  components: { Snackbar, ViewTicket, StatusChip, PriorityChip, EmptyState },
  mixins: [apiMixin, formatMixin],
  data() {
    return {
      constant: constant,
      loading: false,
      dense: false,
      hiddenKeys: [],
      search: '',
      dateFrom: null,
      dateTo: null,
      tickets: [],
      projects: [],
      sprints: [],
      employees: [],
      isshow: false,
      message: '',
      filters: {
        project_id: null,
        sprint_id: null,
        assignee: null,
        status: null,
      },
      statusItems: ['Todo', 'In Progress', 'On Hold', 'Cancelled', 'Done'],
      headers: [
        { title: 'Ticket', key: 'ticket_id', sortable: true },
        { title: 'Title', key: 'name', sortable: true },
        { title: 'Project', key: 'project', sortable: false },
        { title: 'Sprint', key: 'sprint', sortable: false },
        { title: 'Assignees', key: 'assignees', sortable: false },
        { title: 'Priority', key: 'priority', sortable: true },
        { title: 'Status', key: 'status', sortable: true },
        { title: 'Due', key: 'due_date', sortable: true },
        { title: 'Created by', key: 'reporter', sortable: true },
        { title: 'Created', key: 'created_at', sortable: true },
        { title: 'Updated by', key: 'updated_by', sortable: true },
        { title: 'Updated', key: 'updated_at', sortable: true },
        { title: '', key: 'actions', sortable: false, align: 'end' },
      ],
    };
  },
  computed: {
    // Columns the user can hide (skip the actions column + blank titles).
    toggleableHeaders() {
      return this.headers.filter((h) => h.title && h.key !== 'actions');
    },
    visibleHeaders() {
      return this.headers.filter((h) => !this.hiddenKeys.includes(h.key));
    },
    projectItems() {
      return [{ id: null, title: 'All projects' }, ...this.projects.map(p => ({ id: p.id, title: p.title }))];
    },
    // Sprint options, narrowed to the selected project when one is chosen.
    sprintItems() {
      const list = this.filters.project_id
        ? this.sprints.filter(s => s.project_id === this.filters.project_id)
        : this.sprints;
      const opts = [
        { value: null, label: 'All sprints' },
        { value: 'backlog', label: 'Backlog (no sprint)' },
      ];
      list.forEach(s => {
        const prefix = s.project && s.project.key ? `${s.project.key} · ` : '';
        opts.push({ value: s.id, label: `${prefix}${s.name}` });
      });
      return opts;
    },
    assigneeItems() {
      return [{ value: null, title: 'All assignees' }, ...this.employees.map(e => ({ value: e.id, title: e.fullname }))];
    },
    // Apply the From/To due-date range on top of the server-side filters.
    displayTickets() {
      if (!this.dateFrom && !this.dateTo) return this.tickets;
      return this.tickets.filter((t) => {
        if (!t.due_date) return false;
        const due = String(t.due_date).slice(0, 10);
        if (this.dateFrom && due < this.dateFrom) return false;
        if (this.dateTo && due > this.dateTo) return false;
        return true;
      });
    },
  },
  methods: {
    toggleColumn(key) {
      const i = this.hiddenKeys.indexOf(key);
      if (i === -1) this.hiddenKeys.push(key);
      else this.hiddenKeys.splice(i, 1);
    },
    // Make the table search match ticket id, title, project, sprint and assignees.
    filterRow(value, query, item) {
      if (!query) return true;
      const q = query.toLowerCase();
      const raw = item && item.raw ? item.raw : {};
      const haystack = [
        raw.ticket_id,
        raw.name,
        raw.project ? (raw.project.title + ' ' + raw.project.key) : '',
        raw.sprint ? raw.sprint.name : 'backlog',
        (raw.assignees_details || []).map(a => a.fullname).join(' '),
      ].join(' ').toLowerCase();
      return haystack.includes(q);
    },
    notify(msg) {
      this.isshow = true;
      this.message = msg;
      setTimeout(() => { this.isshow = false; }, 1500);
    },
    onProjectChange() {
      // Reset sprint when the chosen project no longer contains it.
      if (this.filters.sprint_id && this.filters.sprint_id !== 'backlog') {
        const stillValid = this.sprints.some(s =>
          s.id === this.filters.sprint_id && (!this.filters.project_id || s.project_id === this.filters.project_id));
        if (!stillValid) this.filters.sprint_id = null;
      }
      this.fetchTickets();
    },
    clearFilters() {
      this.filters = { project_id: null, sprint_id: null, assignee: null, status: null };
      this.search = '';
      this.dateFrom = null;
      this.dateTo = null;
      this.fetchTickets();
    },
    buildParams() {
      const p = {};
      if (this.filters.project_id) p.project_id = this.filters.project_id;
      if (this.filters.sprint_id) p.sprint_id = this.filters.sprint_id;
      if (this.filters.assignee) p.assignee = this.filters.assignee;
      if (this.filters.status) p.status = this.filters.status;
      return p;
    },
    fetchTickets() {
      this.loading = true;
      axios.get(utils._api(this.constant.get_tickets), {
        ...this.authHeaders(),
        params: this.buildParams(),
      })
        .then((response) => {
          this.tickets = response.data.data || [];
        })
        .catch((error) => {
          console.error('Error fetching tickets:', error);
          this.notify('Failed to load tickets');
        })
        .finally(() => { this.loading = false; });
    },
    getProjects() {
      axios.get(utils._api(this.constant.list_projects), this.authHeaders())
        .then((response) => { this.projects = response.data.data || []; })
        .catch(error => console.error('Error fetching projects:', error));
    },
    getSprints() {
      axios.get(utils._api(this.constant.list_all_sprints), this.authHeaders())
        .then((response) => { this.sprints = response.data.data || []; })
        .catch(error => console.error('Error fetching sprints:', error));
    },
    getEmployees() {
      axios.get(utils._api(this.constant.get_active_users), this.authHeaders())
        .then((response) => { this.employees = response.data.data || []; })
        .catch(error => console.error('Error fetching users:', error));
    },
  },
  mounted() {
    this.getProjects();
    this.getSprints();
    this.getEmployees();
    this.fetchTickets();
    // If arrived here from the global search, prefill the search box.
    try {
      const term = localStorage.getItem('int_search_ticket');
      if (term) { this.search = term; localStorage.removeItem('int_search_ticket'); }
    } catch (e) { /* ignore */ }
  },
};
</script>

<style scoped>
.int-tickets-table :deep(th) {
  font-weight: 700 !important;
  color: var(--int-text-strong) !important;
}
.int-tickets-table :deep(td) {
  font-size: 0.85rem;
}
</style>
